
import { useState, useEffect } from 'react';
import { Attendee } from '@/types/attendee';
import { updateAttendance } from '@/lib/supabase/client';
import { usePaymentToggle } from '@/hooks/admin/usePaymentToggle';

export const useAttendeeRow = (
  attendee: Attendee,
  onSilentUpdate?: (attendee: Attendee) => void
) => {
  // Local state for optimistic updates
  const [localAttendance, setLocalAttendance] = useState<boolean>(attendee.attended);
  const [localCheckedInAt, setLocalCheckedInAt] = useState<string | undefined>(attendee.checkedInAt);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // Use the payment toggle hook
  const {
    isPaid,
    isUpdating: isPaymentUpdating,
    showConfirmDialog,
    setShowConfirmDialog,
    pendingPaymentStatus,
    handlePaymentToggleRequest,
    confirmPaymentChange,
    cancelPaymentChange
  } = usePaymentToggle(attendee);
  
  // Combined updating state
  const combinedIsUpdating = isUpdating || isPaymentUpdating;

  // Update local state when props change
  useEffect(() => {
    setLocalAttendance(attendee.attended);
    setLocalCheckedInAt(attendee.checkedInAt);
  }, [attendee.attended, attendee.checkedInAt]);

  const handleDirectUpdate = async (code: string, updates: any) => {
    setIsUpdating(true);

    try {
      // Ensure we have the t_shirt_size field in updates
      const enhancedUpdates = {
        ...updates,
        t_shirt_size: updates.t_shirt_size || attendee.t_shirt_size
      };
      
      console.log('Enhanced updates for direct update:', enhancedUpdates);

      if ('attended' in updates) {
        // Handle attendance update
        setLocalAttendance(updates.attended);
        setLocalCheckedInAt(updates.checkedInAt || undefined);

        // Call the database directly
        const timestamp = updates.attended ? new Date().toISOString() : null;
        await updateAttendance(code, updates.attended, timestamp);

        // Also update silently if callback exists
        if (onSilentUpdate) {
          const updatedAttendee: Attendee = {
            ...attendee,
            attended: updates.attended,
            checkedInAt: updates.attended ? timestamp : undefined,
            t_shirt_size: attendee.t_shirt_size // Preserve t_shirt_size
          };
          onSilentUpdate(updatedAttendee);
        }
      }

      return {
        ...attendee,
        ...enhancedUpdates
      };
    } catch (error) {
      console.error('Error in direct update:', error);
      // Revert optimistic updates on error
      setLocalAttendance(attendee.attended);
      setLocalCheckedInAt(attendee.checkedInAt);
      return null;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    isPaid,
    isUpdating: combinedIsUpdating,
    localAttendance,
    localCheckedInAt,
    handlePaymentToggleRequest,
    handleDirectUpdate,
    showConfirmDialog,
    setShowConfirmDialog,
    pendingPaymentStatus,
    confirmPaymentChange,
    cancelPaymentChange
  };
};
